import React, { useState, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Actions as ApiCallActions } from '../../../redux/apiCall/reducers'
import general from '../../../utils/general'
import Input from '../../../components/input'
import Select from '../../../components/select'
import {
  ValidationMessages
} from '../../../utils/validation'
import {
  ProgramTypeOptions,
  EducationLanguageOptions,
  UserTypeEnum
} from '../../../utils/enums'
import { getMissingStudentFields } from '../../../utils/studentValidation'
import './ApplicationForm.scss'
import Modal from '../../../components/modal'
import useI18n from '../../../i18n/useI18n'
import RoleBasedAccess from '../../../components/role-based-access'

const ApplicationForm = ({ isOpen, onClose, program, universityName, userData, universityId }) => {
  const dispatch = useDispatch()
  const { t } = useI18n()
  const navigate = useNavigate()
  const userInfo = useSelector((state) => state.auth?.userInfo || {})
  const userType = userData?.userType || userInfo?.userType || ''
  const isAgentOrAdmin = userType === UserTypeEnum.AGENT || userType === UserTypeEnum.ADMIN

  const [formData, setFormData] = useState({
    // Başvurduğunuz Bölüm
    university: universityName || '',
    programType: program?.degree || '',
    educationLanguage: program?.language || '',
    department: program?.name || program?.nameEn || '',
    discountedFee: program?.discountedFee || '',
    programId: program?.id || '',

    // Öğrenci seçimi (Agent/Admin için)
    selectedStudentId: '',

    // Gizlilik
    privacyAccepted: false
  })

  const [studentProfile, setStudentProfile] = useState(null)
  const [studentLoading, setStudentLoading] = useState(false)
  const [validationErrors, setValidationErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [programs, setPrograms] = useState([])
  const [programsLoading, setProgramsLoading] = useState(false)
  const [students, setStudents] = useState([])
  const [studentsLoading, setStudentsLoading] = useState(false)
  const [studentWarning, setStudentWarning] = useState('')

  // Öğrenci listesini çek (Agent/Admin için)
  useEffect(() => {
    if (isOpen && isAgentOrAdmin) {
      fetchStudents()
    }
  }, [isOpen, isAgentOrAdmin])

  // Öğrenci profilinden veri çek
  useEffect(() => {
    if (isOpen) {
      if (isAgentOrAdmin && formData.selectedStudentId) {
        // Agent/Admin ise seçilen öğrencinin profilini çek
        fetchStudentProfile(formData.selectedStudentId)
      } else if (!isAgentOrAdmin) {
        // Student ise kendi profilini çek
        fetchStudentProfile()
      } else {
        setStudentProfile(null)
      }
    } else {
      setStudentProfile(null)
    }
  }, [isOpen, isAgentOrAdmin, formData.selectedStudentId])

  // Üniversite programlarını çek
  useEffect(() => {
    if (isOpen && !program && universityId) {
      fetchPrograms()
    }
  }, [isOpen, program, universityId])

  useEffect(() => {
    if (isOpen) {
      if (program) {
        // Program seçiliyse tüm alanları doldur
        setFormData(prev => ({
          ...prev,
          university: universityName || '',
          programType: program.degree || '',
          educationLanguage: program.language || '',
          department: program.name || program.nameEn || '',
          discountedFee: program.discountedFee || '',
          programId: program.id || ''
        }))
      } else {
        // Program seçili değilse (Hero butonundan) sadece üniversite dolu olsun
        setFormData(prev => ({
          ...prev,
          university: universityName || '',
          programType: '',
          educationLanguage: '',
          department: '',
          discountedFee: '',
          programId: ''
        }))
      }
    }
  }, [isOpen, program, universityName])

  const fetchStudents = () => {
    setStudentsLoading(true)
    const queryParams = {}

    // Agent ise sadece kendi öğrencilerini göster
    if (userType === UserTypeEnum.AGENT && userData?.agentId) {
      queryParams.agentId = userData.agentId
    }

    dispatch(
      ApiCallActions.Get({
        url: 'students',
        query: { ...queryParams, limit: 100 },
        showAlertOnError: false,
        showLoading: false,
        onSuccess: ({ data }) => {
          const studentsData = data?.data || data || []
          setStudents(Array.isArray(studentsData) ? studentsData : [])
          setStudentsLoading(false)
        },
        onError: () => {
          setStudents([])
          setStudentsLoading(false)
        }
      })
    )
  }

  const fetchStudentProfile = (studentId = null) => {
    setStudentLoading(true)
    const url = studentId ? `students/${studentId}` : 'students/me'

    dispatch(
      ApiCallActions.Get({
        url,
        showAlertOnError: false,
        showLoading: false,
        onSuccess: ({ data }) => {
          const student = data?.data || data
          setStudentProfile(student)
          const missing = getMissingStudentFields(student || {})
          setStudentWarning(missing.length ? missing.join(', ') : '')
          setStudentLoading(false)
        },
        onError: () => {
          setStudentProfile(null)
          setStudentWarning('')
          setStudentLoading(false)
        }
      })
    )
  }

  const fetchPrograms = () => {
    if (!universityId) return

    setProgramsLoading(true)
    dispatch(
      ApiCallActions.Get({
        url: `universities/${universityId}/programs`,
        showAlertOnError: false,
        showLoading: false,
        onSuccess: ({ data }) => {
          const programsData = data?.data || data || []
          setPrograms(Array.isArray(programsData) ? programsData : [])
          setProgramsLoading(false)
        },
        onError: () => {
          setPrograms([])
          setProgramsLoading(false)
        }
      })
    )
  }

  // Programları filtrele (Program Türü ve Eğitim Dili'ne göre)
  const filteredPrograms = useMemo(() => {
    if (!programs.length) return []

    return programs.filter(p => {
      const matchesDegree = !formData.programType || p.degree === formData.programType
      const matchesLanguage = !formData.educationLanguage || p.language === formData.educationLanguage
      return matchesDegree && matchesLanguage
    })
  }, [programs, formData.programType, formData.educationLanguage])

  // Program Türü seçenekleri (programlardan unique değerler)
  const programTypeOptions = useMemo(() => {
    if (!programs.length) return ProgramTypeOptions

    const uniqueDegrees = [...new Set(programs.map(p => p.degree).filter(Boolean))]
    return uniqueDegrees.map(degree => {
      const option = ProgramTypeOptions.find(opt => opt.value === degree)
      return option || { value: degree, label: degree }
    })
  }, [programs])

  // Eğitim Dili seçenekleri (programlardan unique değerler)
  const educationLanguageOptions = useMemo(() => {
    if (!programs.length) return EducationLanguageOptions

    const uniqueLanguages = [...new Set(programs.map(p => p.language).filter(Boolean))]
    return uniqueLanguages.map(language => {
      const option = EducationLanguageOptions.find(opt => opt.value === language)
      return option || { value: language, label: language }
    })
  }, [programs])

  // Bölüm seçenekleri (filtrelenmiş programlardan veya program prop'undan)
  const departmentOptions = useMemo(() => {
    // Eğer program prop'u varsa, direkt ondan oluştur
    if (program) {
      return [{
        value: program.name || program.nameEn || '',
        label: program.name || program.nameEn || ''
      }]
    }

    // Yoksa filtrelenmiş programlardan oluştur
    if (!filteredPrograms.length) return []

    return filteredPrograms.map(p => ({
      value: p.name || p.nameEn || '',
      label: p.name || p.nameEn || ''
    }))
  }, [filteredPrograms, program])


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    const newFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    }

    // Program Türü veya Eğitim Dili değiştiğinde, Bölüm ve programId'yi sıfırla
    if (name === 'programType' || name === 'educationLanguage') {
      newFormData.department = ''
      newFormData.programId = ''
      newFormData.discountedFee = ''
    }

    // Bölüm seçildiğinde, programId'yi ayarla
    if (name === 'department' && value) {
      const selectedProgram = filteredPrograms.find(p => {
        const programName = p.name || p.nameEn || ''
        return programName === value
      })
      if (selectedProgram) {
        newFormData.programId = selectedProgram.id || ''
        newFormData.discountedFee = selectedProgram.discountedFee || ''
      }
    }

    setFormData(newFormData)

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handleValidationChange = (fieldName, isValid, errorMessage) => {
    setValidationErrors(prev => ({
      ...prev,
      [fieldName]: isValid ? '' : errorMessage
    }))
  }

  const validateForm = () => {
    const errors = {}

    // Program seçimi zorunlu
    if (!formData.programId) {
      errors.programId = ValidationMessages.REQUIRED
    }

    // Agent/Admin ise öğrenci seçimi zorunlu
    if (isAgentOrAdmin && !formData.selectedStudentId) {
      errors.selectedStudentId = ValidationMessages.REQUIRED
    }

    // Öğrenci profili yüklenmiş olmalı
    if (!studentProfile || !studentProfile.id) {
      errors.studentProfile = isAgentOrAdmin
        ? t('APPLICATION_FORM_VALIDATION_STUDENT_REQUIRED')
        : t('APPLICATION_FORM_VALIDATION_STUDENT_PROFILE_ERROR')
    }

    // Gizlilik politikası onayı zorunlu
    if (!formData.privacyAccepted) {
      errors.privacyAccepted = ValidationMessages.REQUIRED
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      general.notificationError(t('APPLICATION_FORM_VALIDATION_FILL_REQUIRED_FIELDS'))
      return
    }

    const missing = getMissingStudentFields(studentProfile || {})
    if (missing.length) {
      const msg = isAgentOrAdmin
        ? `${t('APPLICATION_FORM_MISSING_FIELDS_AGENT')} ${missing.join(', ')}`
        : `${t('APPLICATION_FORM_MISSING_FIELDS_STUDENT')} ${missing.join(', ')}`
      general.notificationError(msg)
      return
    }

    if (!studentProfile || !studentProfile.id) {
      general.notificationError(isAgentOrAdmin
        ? t('APPLICATION_FORM_VALIDATION_STUDENT_REQUIRED')
        : t('APPLICATION_FORM_VALIDATION_STUDENT_PROFILE_ERROR'))
      return
    }

    setIsSubmitting(true)

    try {
      const programId = formData.programId || program?.id
      if (!programId) {
        general.notificationError(t('APPLICATION_FORM_VALIDATION_PROGRAM_REQUIRED'))
        setIsSubmitting(false)
        return
      }

      // Sadece gerekli alanları gönder
      const submitData = {
        studentId: studentProfile.id,
        programId: programId,
        applicationStatus: 'draft',
        applicationDate: new Date().toISOString().split('T')[0],
        applicationYear: new Date().getFullYear()
      }

      // Opsiyonel alanlar (eğer varsa)
      if (isAgentOrAdmin && userData?.agentId) {
        submitData.agentId = userData.agentId
      } else if (userData?.agentId) {
        submitData.agentId = userData.agentId
      }

      dispatch(
        ApiCallActions.Post({
          url: 'applications',
          data: submitData,
          showAlertOnError: true,
          showLoading: true,
          onSuccess: () => {
            general.messageSuccess(t('APPLICATION_FORM_SUCCESS_SUBMITTED'))
            handleClose()
          },
          onError: ({ errorMessage }) => {
            general.notificationError(errorMessage || t('APPLICATION_FORM_ERROR_SUBMIT_FAILED'))
          },
          callback: () => setIsSubmitting(false)
        })
      )
    } catch (error) {
      general.notificationError(t('APPLICATION_FORM_ERROR_GENERIC'))
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        university: universityName || '',
        programType: program?.degree || '',
        educationLanguage: program?.language || '',
        department: program?.name || program?.nameEn || '',
        discountedFee: program?.discountedFee || '',
        programId: program?.id || '',
        selectedStudentId: '',
        privacyAccepted: false
      })
      setValidationErrors({})
      onClose()
    }
  }

  // Öğrenci seçenekleri (Agent/Admin için)
  const studentOptions = useMemo(() => {
    return students.map(student => {
      const user = student?.user || {}
      const name = `${user.firstName || ''} ${user.lastName || ''}`.trim()
      const label = name || user.email || student.email || student.id
      return {
        value: student.id,
        label: label
      }
    })
  }, [students])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])



  if (!isOpen) return null

  // Ensure document.body exists before using portal
  if (typeof document === 'undefined' || !document.body) {
    return null
  }

  const modalContent = (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('APPLICATION_FORM_TITLE')}
      subtitle={program?.name || program?.nameEn || t('PROGRAMS_ENTITY')}
      icon="zmdi-info-outline"
      size="large"
    >

      <form onSubmit={handleSubmit} className="application-form">
        {/* Başvurduğunuz Bölüm */}
        <div className="form-section">
          <h4 className="section-title">{t('APPLICATION_FORM_SECTION_APPLYING_DEPARTMENT')}</h4>
          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label>{t('APPLICATION_FORM_FIELD_UNIVERSITY')} <span className="required">*</span></label>
                <Input
                  name="university"
                  value={formData.university}
                  onChange={handleChange}
                  required
                  disabled
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label>{t('APPLICATION_FORM_FIELD_PROGRAM_TYPE')} <span className="required">*</span></label>
                <Select
                  name="programType"
                  value={formData.programType}
                  onChange={handleChange}
                  options={(() => {
                    if (program) {
                      // Program seçiliyse, program'dan gelen değeri options'a ekle
                      const programTypeValue = program.degree || formData.programType
                      const existingOption = ProgramTypeOptions.find(opt => opt.value === programTypeValue)
                      if (existingOption) {
                        return ProgramTypeOptions
                      } else if (programTypeValue) {
                        // Eğer enum'da yoksa, program'dan gelen değeri ekle
                        return [...ProgramTypeOptions, { value: programTypeValue, label: programTypeValue }]
                      }
                      return ProgramTypeOptions
                    }
                    return programTypeOptions
                  })()}
                  placeholder={t('APPLICATION_FORM_PLACEHOLDER_SELECT_PROGRAM_TYPE')}
                  required
                  disabled={!!program}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label>{t('APPLICATION_FORM_FIELD_EDUCATION_LANGUAGE')} <span className="required">*</span></label>
                <Select
                  name="educationLanguage"
                  value={formData.educationLanguage}
                  onChange={handleChange}
                  options={(() => {
                    if (program) {
                      // Program seçiliyse, program'dan gelen değeri options'a ekle
                      const languageValue = program.language || formData.educationLanguage
                      const existingOption = EducationLanguageOptions.find(opt => opt.value === languageValue)
                      if (existingOption) {
                        return EducationLanguageOptions
                      } else if (languageValue) {
                        // Eğer enum'da yoksa, program'dan gelen değeri ekle
                        return [...EducationLanguageOptions, { value: languageValue, label: languageValue }]
                      }
                      return EducationLanguageOptions
                    }
                    return educationLanguageOptions
                  })()}
                  placeholder={t('APPLICATION_FORM_PLACEHOLDER_SELECT_EDUCATION_LANGUAGE')}
                  required
                  disabled={!!program}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label>{t('APPLICATION_FORM_FIELD_DEPARTMENT')} <span className="required">*</span></label>
                <Select
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  options={departmentOptions}
                  placeholder={programsLoading ? t('COMMON_LOADING') : (formData.programType && formData.educationLanguage ? t('APPLICATION_FORM_PLACEHOLDER_SELECT_DEPARTMENT') : t('APPLICATION_FORM_PLACEHOLDER_SELECT_DEPARTMENT_FIRST'))}
                  required
                  disabled={!!program || programsLoading || !formData.programType || !formData.educationLanguage}
                />
                {validationErrors.programId && (
                  <div className="invalid-feedback" style={{ display: 'block', color: '#dc3545', fontSize: '12px', marginTop: '0.25rem' }}>
                    {validationErrors.programId}
                  </div>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label>{t('APPLICATION_FORM_FIELD_DISCOUNTED_FEE')}</label>
                <Input
                  name="discountedFee"
                  value={program && formData.discountedFee ? `$${formData.discountedFee.toLocaleString()}` : formData.discountedFee}
                  onChange={handleChange}
                  placeholder={t('APPLICATION_FORM_FIELD_FEE_INFO')}
                  disabled={!!program}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Öğrenci Seçimi (Agent/Admin için) */}
        {isAgentOrAdmin && (
          <div className="form-section">
            <h4 className="section-title">{t('APPLICATION_FORM_SECTION_STUDENT_SELECTION')}</h4>
            <div className="row">
              <div className="col-md-12">
                <div className="form-group">
                  <label>{t('APPLICATION_FORM_FIELD_STUDENT')} <span className="required">*</span></label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <Select
                        name="selectedStudentId"
                        value={formData.selectedStudentId}
                        onChange={handleChange}
                        options={studentOptions}
                        placeholder={studentsLoading ? t('COMMON_LOADING') : t('APPLICATION_FORM_PLACEHOLDER_SELECT_STUDENT')}
                        required
                        disabled={studentsLoading}
                      />
                    </div>
                     <RoleBasedAccess allowedRoles={[UserTypeEnum.AGENT]}>
                    <button
                      type="button"
                      className="btn btn-success btn-icon btn-round"
                      onClick={() => window.open('/students/new', '_blank')}
                      title={t('APPLICATION_FORM_ADD_STUDENT')}
                      style={{ flexShrink: 0, marginTop: '2px' }}
                    >
                      <i className="zmdi zmdi-plus"></i>
                    </button>
                    <button
                      type="button"
                      className="btn btn-info btn-icon btn-round"
                      onClick={fetchStudents}
                      disabled={studentsLoading}
                      title={t('COMMON_ACTIONS_REFRESH')}
                      style={{ flexShrink: 0, marginTop: '2px' }}
                    >
                      <i className={`zmdi zmdi-refresh${studentsLoading ? ' zmdi-hc-spin' : ''}`}></i>
                    </button>
                    </RoleBasedAccess>
                  </div>
                  {validationErrors.selectedStudentId && (
                    <div className="invalid-feedback" style={{ display: 'block', color: '#dc3545', fontSize: '12px', marginTop: '0.25rem' }}>
                      {validationErrors.selectedStudentId}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Öğrenci Bilgileri - Sadece Görüntüleme */}
        <div className="form-section">
          <h4 className="section-title">{t('APPLICATION_FORM_SECTION_STUDENT_INFO')}</h4>
          {!isAgentOrAdmin && (
            <p className="section-description" style={{ marginBottom: '20px', color: '#666', fontSize: '14px' }}>
              {t('APPLICATION_FORM_STUDENT_INFO_DESCRIPTION')}{' '}
              <a
                href="/profile?tab=student"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#007bff', textDecoration: 'underline', fontWeight: '500' }}
              >
                {t('APPLICATION_FORM_STUDENT_INFO_VISIT_PROFILE')}
              </a>.
            </p>
          )}
          {studentLoading ? (
            <div className="text-center" style={{ padding: '20px' }}>
              <p>{t('APPLICATION_FORM_STUDENT_LOADING')}</p>
            </div>
          ) : studentProfile ? (
            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label>{t('APPLICATION_FORM_FIELD_DATE_OF_BIRTH')}</label>
                  <Input
                    value={studentProfile.dateOfBirth || '-'}
                    disabled
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label>{t('APPLICATION_FORM_FIELD_NATIONALITY')}</label>
                  <Input
                    value={studentProfile.nationality || '-'}
                    disabled
                  />
                </div>
              </div>
              <div className="col-md-12">
                <div className="form-group">
                  <label>{t('APPLICATION_FORM_FIELD_ADDRESS')}</label>
                  <Input
                    value={studentProfile.address || '-'}
                    disabled
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label>{t('APPLICATION_FORM_FIELD_CITY')}</label>
                  <Input
                    value={studentProfile.city || '-'}
                    disabled
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label>{t('APPLICATION_FORM_FIELD_COUNTRY')}</label>
                  <Input
                    value={studentProfile.country || '-'}
                    disabled
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label>{t('APPLICATION_FORM_FIELD_POSTAL_CODE')}</label>
                  <Input
                    value={studentProfile.postalCode || '-'}
                    disabled
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label>{t('APPLICATION_FORM_FIELD_EMERGENCY_NAME')}</label>
                  <Input
                    value={studentProfile.emergencyContactName || '-'}
                    disabled
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label>{t('APPLICATION_FORM_FIELD_EMERGENCY_PHONE')}</label>
                  <Input
                    value={studentProfile.emergencyContactPhone || '-'}
                    disabled
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label>{t('APPLICATION_FORM_FIELD_EMERGENCY_RELATION')}</label>
                  <Input
                    value={studentProfile.emergencyContactRelation || '-'}
                    disabled
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label>{t('APPLICATION_FORM_FIELD_EDUCATION_LEVEL')}</label>
                  <Input
                    value={studentProfile.educationLevel || '-'}
                    disabled
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label>{t('APPLICATION_FORM_FIELD_GPA')}</label>
                  <Input
                    value={studentProfile.gpa !== undefined && studentProfile.gpa !== null ? studentProfile.gpa : '-'}
                    disabled
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label>{t('APPLICATION_FORM_FIELD_ENGLISH_LEVEL')}</label>
                  <Input
                    value={studentProfile.languageProficiency?.english || '-'}
                    disabled
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label>{t('APPLICATION_FORM_FIELD_TURKISH_LEVEL')}</label>
                  <Input
                    value={studentProfile.languageProficiency?.turkish || '-'}
                    disabled
                  />
                </div>
              </div>
              <div className="col-md-12">
                <div className="form-group">
                  <label>{t('APPLICATION_FORM_FIELD_PASSPORT_FILE')}</label>
                  {studentProfile.passportFile ? (
                    <a href={studentProfile.passportFile} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', textDecoration: 'underline' }}>
                      {t('APPLICATION_FORM_LINK_VIEW_PASSPORT')}
                    </a>
                  ) : (
                    <Input value="-" disabled />
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label>{t('APPLICATION_FORM_FIELD_TRANSCRIPT')}</label>
                  {studentProfile.highschoolTranscript ? (
                    <a href={studentProfile.highschoolTranscript} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', textDecoration: 'underline' }}>
                      {t('APPLICATION_FORM_LINK_VIEW_TRANSCRIPT')}
                    </a>
                  ) : (
                    <Input value="-" disabled />
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label>{t('APPLICATION_FORM_FIELD_DIPLOMA')}</label>
                  {studentProfile.highschoolDiploma ? (
                    <a href={studentProfile.highschoolDiploma} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', textDecoration: 'underline' }}>
                      {t('APPLICATION_FORM_LINK_VIEW_DIPLOMA')}
                    </a>
                  ) : (
                    <Input value="-" disabled />
                  )}
                </div>
              </div>
              {(studentProfile.otherFiles?.cv || studentProfile.otherFiles?.motivationLetter) && (
                <>
                  {studentProfile.otherFiles?.cv && (
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>{t('APPLICATION_FORM_FIELD_CV')}</label>
                        <a href={studentProfile.otherFiles.cv} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', textDecoration: 'underline' }}>
                          {t('APPLICATION_FORM_LINK_VIEW_CV')}
                        </a>
                      </div>
                    </div>
                  )}
                  {studentProfile.otherFiles?.motivationLetter && (
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>{t('APPLICATION_FORM_FIELD_MOTIVATION_LETTER')}</label>
                        <a href={studentProfile.otherFiles.motivationLetter} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', textDecoration: 'underline' }}>
                          {t('APPLICATION_FORM_LINK_VIEW_MOTIVATION_LETTER')}
                        </a>
                      </div>
                    </div>
                  )}
                </>
              )}
              {studentProfile.additionalInfo?.hobbies && (
                <div className="col-md-12">
                  <div className="form-group">
                    <label>{t('APPLICATION_FORM_FIELD_HOBBIES')}</label>
                    <Input
                      value={studentProfile.additionalInfo.hobbies}
                      disabled
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Hata mesajını sadece şu durumlarda göster:
            // 1. Student kullanıcısı için: Profil yüklenemediğinde
            // 2. Agent/Admin kullanıcısı için: Öğrenci seçildikten sonra ve profil yüklenemediğinde
            (!isAgentOrAdmin || (isAgentOrAdmin && formData.selectedStudentId)) && (
              <div className="alert alert-warning" style={{ marginTop: '10px' }}>
                {t('APPLICATION_FORM_STUDENT_PROFILE_ERROR')}
              </div>
            )
          )}
          {validationErrors.studentProfile && (
            <div className="invalid-feedback" style={{ display: 'block', color: '#dc3545', fontSize: '12px', marginTop: '0.25rem' }}>
              {validationErrors.studentProfile}
            </div>
          )}
        </div>

        {/* Gizlilik Politikası */}
        <div className="form-section">
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="privacyAccepted"
                checked={formData.privacyAccepted}
                onChange={handleChange}
                required
              />
              <span>{t('APPLICATION_FORM_PRIVACY_CHECKBOX')} <span className="required">*</span></span>
            </label>
            {validationErrors.privacyAccepted && (
              <div className="invalid-feedback" style={{ display: 'block', color: '#dc3545', fontSize: '12px', marginTop: '0.25rem' }}>
                {validationErrors.privacyAccepted}
              </div>
            )}
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t('APPLICATION_FORM_BUTTON_CANCEL')}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? t('APPLICATION_FORM_BUTTON_SAVING') : t('APPLICATION_FORM_BUTTON_SAVE')}
          </button>
        </div>
      </form>
    </Modal>
  )

  return createPortal(modalContent, document.body)
}

export default ApplicationForm

